import { message } from 'antd';
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useReducer,
  useMemo
} from 'react';
import { useNavigate } from 'react-router-dom';
import Api from '../../../api/Api';
import { StoreContext } from '../../../store/store';
import styles from '../Style/AnalyticsPanel.module.scss';
import ExtremeValues from './Plot/ExtremeValues';
import Histogram from './Plot/Histogram';
import Persistence from './Plot/Persistence';
import RosePlot from './Plot/RosePlot';
import ScatterPlot from './Plot/ScatterPlot';
import ScatterTable from './Plot/ScatterTable';
import TSPlot from './Plot/TSPlot';
import {
  IAdvancedAnalyticsApiPayload,
  IGetBasisChartV2ApiPayload,
  IGetJointScatterPlotV2ApiPayload,
  IGetRosePlotV2ApiPayload,
  IScatterTableApiPayload,
} from '../../../typings/chart';
import { IBestDataset } from '../../../typings/analytics';
import {
  DownloadInfoItem,
  IAnalytic,
  IPreviewAnalytic,
  IPreviewData,
  IRosePlotPreviewData,
  RosePlotChart,
} from '../../../typings/analytics';
import { Spin } from 'antd';
import {
  IDatasetBrief,
  IEnrichedMetadata,
} from '../../../typings/metadata';
import moment from 'moment';
import { DT0Format } from '../../../generic/StaticVariable';
import { DATASET_CATEGORIES } from '../../../generic/filterBestDataset';
import { MarkerItem } from '../../../typings/home';
import { setDateRangeLimit } from '../../../generic/dateRange';
import {
  AnalyticsList,
  UserGroupPermissions,
} from '../../../typings/userGroupPermissions';
import { analyticsUserInput } from '../../../generic/analyticsUserInput';
import { IAnalytcisUserInput } from '../../../typings/IAnalyticsUserInput';
import TidalLevel from './Plot/TidalLevel';
import Statistics from './Plot/Statistics';
import Exceedance from './Plot/Exceedance';
import { useIsDwf, useUserProvider } from '../../../generic/hooks';
import { useIsTnw } from '../../../generic/hooks';
import { withLoading } from '../../../components/Loading/withLoading';
import LoadingContext from '../../../context/loadingContext/LoadingContext';
import { useData } from '../Store/DatasetContext';
import { EVENT_ANALYTICS_TYPE_SELECTED, EVENT_ANALYTICS_TYPE_UPDATED, trackUpdate } from '../../../generic/matomoEvents';
import { datasetType, GLOBAL_HD_DTU_ID, ITEM, TabKey } from '../../../generic/common';
import { useSignalR } from '../../../generic/hooks';

export enum ADVANCED_ANALYTICS {
  PERSISTENCE_WEATHERWINDOW = 'persistence (weather windows)',
  EXTREME_VALUES = 'extreme values',
  HISTOGRAM = 'histogram',
  EXCEEDANCE = 'exceedance probability',
  ALTIMETER_COMPARISON = 'altimeter comparison',
  TIDAL_LEVEL = 'tidal parameters',
  STATISTICS = 'monthly statistics',
  SCATTER_PLOT_INSITU = 'scatter_comparison'
}

export enum PREPROCESSED_ANALYTICS {
  XLS = 'Pre-processed Analytics',
}

export enum BASIC_ANALYTICS {
  TIME_SERIES = 'Time Series',
  ROSE_PLOT = 'Rose Plot',
  SCATTER_PLOT = 'Scatter Plot'
}

export const DEFAULT_ANALYTICS = BASIC_ANALYTICS.TIME_SERIES.toLowerCase()
export const DEFAULT_VALIDATION = 'satellite'

export const getFullExtractionTimeOutError = () => {
  return (
    <>
      <div>
        {'We are sorry, the data processing failed.'}
        <div></div>
        {'Please make a new point and try again.'}{' '}
      </div>
    </>
  );
};

export function getPointNameAndAcronym(
  datasetId: string,
  briefs: IDatasetBrief[],
  activePoint?: MarkerItem,
  datasetY?: string
) {
  const brief = briefs.find((b: IDatasetBrief) => b.Id === datasetId);
  const pointName =
    activePoint && activePoint.label ? activePoint.label + '_' : '';
  const acronym = brief !== undefined ? brief.Acronym + '_' : '';
  const briefY = datasetY
    ? briefs.find((b: IDatasetBrief) => b.Id === datasetY)
    : undefined;
  const acronymY = briefY !== undefined ? briefY.Acronym + '_' : '';
  return pointName + acronym + acronymY;
}

export default function AnalyticsPanel(props: {
  downloadItems: DownloadInfoItem[];
  coordinates: number[];
  previewDatasetId: string;
  userGroupPermissions: UserGroupPermissions;
  pointProps: string;
  pointName: string;
  gridCellSize: string;
  waterDepth: string;
  point?: MarkerItem
  category: string | string[];
  setDatasetHeadline: React.Dispatch<React.SetStateAction<string>>;
  onMeshControl: (value: string) => any;
  setDatasetIsOfflineWarningVisible: React.Dispatch<React.SetStateAction<boolean>>;
  onChangeActiveKey: (item: any, key?: TabKey) => void
}) {
  const {
    markerCollections,
    setMarkerCollections,
    previewAnalytics,
    setPreviewAnalytics,
    datasetBriefs,
    setShowLogin,
    fwePoints,
    setIsChangeDatasetWarningVisible,
    user,
    timeStepInterval,
    setEnrichedMetadata,
    enrichedMetadata,
    showMesh
  } = useContext(StoreContext);
  const { point } = props
  const loadingContext = useContext(LoadingContext)
  const navigate = useNavigate()
  const [datasets, setDatasets] = useState<IBestDataset[]>([]);
  const [activePlotAnalytics, setActivePlotAnalytics] = useState<string[]>([]);
  const [previousPointName, setPreviousPointName] = useState('')
  const [yearPreview, setYearPreview] = useState<IPreviewData | null>(null);
  const [yearPreviewError, setYearPreviewError] = useState('');
  const [rosePlotPreview, setRosePlotPreview] =
    useState<IRosePlotPreviewData | null>(null);
  const [rosePlotPreviewError, setRosePlotPreviewError] = useState('')
  const [loadingPreviewTsPlot, setLoadingPreviewTsPlot] = useState(false);

  const [analyticsDefinition, setAnalyticsDefinition] = useState('');
  const [availableAnalytics, setAvailableAnalytics] = useState<IAnalytic[]>([]);

  const [scatterTableData, setScatterTableData] = useState<any[]>([]);
  const [scatterTableLinks, setScatterTableLinks] = useState<string[]>([]);

  const [tidalLevelLinks, setTidalLevelLinks] = useState<string[]>([])

  const [isAuthorized] = useUserProvider();
  const [datasetIsOffline, setDatasetIsOffline] = useState(false)
  const [isForAllAnalytics, setIsForAllAnalytics] = useState(false)
  const [analyticsDateTime, setAnalyticsDateTime] = useState<any[]>([])
  const [defaultRosePlotDirection, setDefaultRosePlotDirection] = useState<number>(1)

  const isTnw = useIsTnw(props.previewDatasetId) && user.authenticated
  const isDwf = useIsDwf(props.previewDatasetId) && user.authenticated
  const currentTimeStep = useRef('60')
  const basicAnalytics = [BASIC_ANALYTICS.TIME_SERIES.toString(), BASIC_ANALYTICS.ROSE_PLOT.toString(), BASIC_ANALYTICS.SCATTER_PLOT.toString()];
  const firstPreviewAnalytics = [BASIC_ANALYTICS.TIME_SERIES.toString()];
  const { dispatch } = useData()
  const basicAndPreprocessedAnalytics = basicAnalytics; //.concat(preprocessAnalytics) // remove the 'Pre-processed…' from Analytics (as it is now replaced by 'Reports'

  const [userInput, updateUserInput] = useReducer(
    (state: IAnalytcisUserInput, update: Partial<IAnalytcisUserInput>) => ({
      ...state,
      ...update,
    }),
    analyticsUserInput
  );

  const pointOptions = useMemo(() => {
    const points = markerCollections?.map((item) => ({label : item.label, value: item.id}))
    return points ?? []
  }, [markerCollections])

  const handleChangePoint = (item: any) => {
    props.onChangeActiveKey(item, ITEM.ANALYTICS)
  }

  const { messageReceived, receivedMessageText, resetMessageState } = useSignalR("AnalyticsReceiveMessage", "JobResult");

  useEffect(() => {
    updateUserInput({ ...analyticsUserInput });
  }, [props.previewDatasetId]);

  useEffect(() => {
    activePlotAnalytics.length === 0 && setActivePlotAnalytics(firstPreviewAnalytics)
  }, [])

  useEffect(() => {
    if (isAuthorized) {
      //When user logs out we need to remove adavanced and pre-processed analytics (if there any)
      setActivePlotAnalytics((previousActivePlot: string[]) =>
        previousActivePlot.filter((x) => firstPreviewAnalytics.includes(x))
      );
    }
  }, [isAuthorized, point]);

  const getOneYearPreviewSeriesData = useCallback(
    async (datasetId: string) => {
      try {
        setYearPreviewError('');
        setRosePlotPreviewError('')
        setYearPreview(null);
        setRosePlotPreview(null);
        setDatasetIsOffline(false)
        setLoadingPreviewTsPlot(true);
        setErrorRosePlot('')
        setErrorScatterPlot('')
        const metadata = await Api.metadata.getMetadata(datasetId);

        if (!metadata) {
          message.error('Failed to get metadata for ' + datasetId);
          setYearPreviewError('Failed to return preview data');
          return;
        }

        if (metadata.IsOffline) {
          setDatasetIsOffline(true)
          props.setDatasetIsOfflineWarningVisible(true)
          setYearPreviewError('Dataset is offline');
          return
        }

        const timeRange = metadata.TimeDefinition?.split(';');

        if (!timeRange || timeRange.length !== 2) {
          message.error('error timedefinition metadata format' + datasetId)
          setYearPreviewError('Failed to return preview data');
          return;
        }
        setAnalyticsDefinition(metadata.AnalyticsDefinition)
        const previewData = previewAnalytics.find(
          (pd: IPreviewAnalytic) => pd.datasetId === datasetId
        )
        const coordinatesUnchanged =
          previewData !== undefined &&
          previewData.coordinates &&
          previewData.coordinates.length === 2 &&
          props.coordinates.length === 2 &&
          previewData.coordinates[0] === props.coordinates[0] &&
          previewData.coordinates[1] === props.coordinates[1];

        const timeStepIntervalUnchanged = currentTimeStep.current === timeStepInterval

        // Check if already create a preview chart and point name not changed
        if (previewData && previewData.data && coordinatesUnchanged && timeStepIntervalUnchanged && props.pointName === previousPointName) {
          setYearPreview(previewData.data);
          previewData.rosePlotPreviewdata ?
            setRosePlotPreview(previewData.rosePlotPreviewdata) :
            setRosePlotPreview(null)
        } else {
          const timeRange = metadata.TimeDefinition?.split(';');
          currentTimeStep.current = timeStepInterval

          if (!timeRange) return

          let data;

          try {
            data = await Api.chart.getBasicChartV2(
              crypto.randomUUID(),
              point?.label ?? '',
              datasetId,
              moment(timeRange[1]).subtract(1, 'year').format(DT0Format),
              timeRange[1],
              props.coordinates.join(),
              datasetId === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60
            );

            if (!data) {
              setYearPreviewError('Failed to return preview data');
              return;
            } else if (data.Error) {
              setYearPreviewError(data.Error.ErrorMessage);
              return;
            }

            setYearPreview({
              ...data,
              ItemsInfo: metadata.ItemsInfo,
              DefaultDirectionShortName: metadata.DefaultDirectionShortName,
              DefaultValueShortName: metadata.DefaultValueShortName,
            });
          } catch (error) {
            setYearPreviewError('Failed to return preview data');
          }

          try {
            const defaultShortName = metadata?.ItemsInfo?.find((item: any) => item.ShortName === metadata.DefaultValueShortName)?.Name ?? metadata.ItemsInfo[0].Name
            const formattedDefaultShortName = defaultShortName.replace(/ /g, "_");const getTimeSeriesShortName = data?.Data?.Items?.map((item : any) => {
              return parseFloat(item[formattedDefaultShortName])
            }).filter((value: any) => !isNaN(value) && value !== null);

            const maxValue = getTimeSeriesShortName.length > 0 ? Math.max(...getTimeSeriesShortName) : 1;
            const rosePlotDirection = parseFloat((maxValue/8).toFixed(2))
            setDefaultRosePlotDirection(rosePlotDirection)

            const rosePlotPreviewData = await Api.chart.getRosePlotV2(
              crypto.randomUUID(),
              point?.label ?? '',
              datasetId,
              moment(timeRange[1]).subtract(1, 'year').format(DT0Format),
              timeRange[1],
              props.coordinates.join(),
              datasetId === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60,
              rosePlotDirection
            );

            if (rosePlotPreviewData?.RosePlot === null){
              setRosePlotPreviewError('Failed to return preview rose data')
            }

            if (rosePlotPreviewData) {
              setRosePlotPreview({
                RosePlot: rosePlotPreviewData,
                ItemsInfo: metadata.ItemsInfo,
                DefaultDirectionShortName: metadata.DefaultDirectionShortName,
                DefaultValueShortName: metadata.DefaultValueShortName,
              });
              setPreviewAnalytics([
                ...previewAnalytics,
                {
                  datasetId: datasetId,
                  data: data.Result,
                  ItemsInfo: metadata.ItemsInfo,
                  DefaultDirectionShortName: metadata.DefaultDirectionShortName,
                  DefaultValueShortName: metadata.DefaultValueShortName,
                  rosePlotPreviewdata: {
                    RosePlot: rosePlotPreviewData,
                    ItemsInfo: metadata.ItemsInfo,
                  },
                  coordinates: props.coordinates,
                },
              ]);
            }
          } catch (error) {
            console.error("Error fetching rose plot preview:", error);
            setRosePlotPreviewError('Failed to return preview rose data')
          }

          try {
            if (enrichedMetadata.length === 0 || previousPointName !== props.pointName) {
              const createFullExtractionTasks = await Api.service.createFullExtractionTasks(
                [metadata],
                props.coordinates,
                props.pointName,
                props.gridCellSize,
                props.waterDepth,
                datasetBriefs,
                fwePoints,
                datasetId === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60
              );

              setEnrichedMetadata(createFullExtractionTasks);
            } else {
              const datasetTask = enrichedMetadata.find(
                (task: IEnrichedMetadata) =>
                  task.DatasetId === datasetId &&
                  task.CoordinatesString === props.coordinates.join()
              );

              if (!datasetTask) {
                const newMetadata = await Api.metadata.getMetadata(datasetId);

                if (newMetadata) {
                  const createFullExtractionTasks = await Api.service.createFullExtractionTasks(
                    [newMetadata],
                    props.coordinates,
                    props.pointName,
                    props.gridCellSize,
                    props.waterDepth,
                    datasetBriefs,
                    fwePoints,
                    datasetId === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60
                  );

                  const enrichedMDs = enrichedMetadata.concat(createFullExtractionTasks);
                  setEnrichedMetadata(enrichedMDs);
                }
              }
            }
          } catch (error) {
            console.error("Error during extraction tasks:", error);
          }
          setPreviousPointName(props.pointName)
        }

      } catch (error) {
        console.error("Error in getOneYearPreviewSeriesData:", error);
      } finally{

        setLoadingPreviewTsPlot(false)
      }
    },
    [props.coordinates, enrichedMetadata, datasetBriefs, point, props.pointName, previousPointName, timeStepInterval]
  );

  const handleAnalyticsTypeChanged = useCallback((analytic: IAnalytic) => {
    if (analytic.disabled === false && !isUpdating ) {
      setActivePlotAnalytics([analytic.id]);
      trackUpdate(EVENT_ANALYTICS_TYPE_SELECTED, props.coordinates, props.previewDatasetId, analytic.id.toLowerCase())
    } else if (!isAuthorized) {
      setShowLogin(true);
    }
  }, [props.coordinates, props.previewDatasetId])

  const categoryDataset = useMemo(() => {
    const selectedDataset: IBestDataset[] = []
    props.downloadItems.forEach(element => {
      const options = element.options.map((item) => {
        return {
          id: item.id,
          label: datasetType[element.radioKey],
          value: item.value
        }
      })
      selectedDataset.push(...options)
    });
    if (selectedDataset.length > 0) {
      return selectedDataset
    }
    return []
  }, [props.category, props.downloadItems, props.coordinates])

  function onDatasetValueChange(item: DownloadInfoItem) {
    const idx = point!.downloadPage!.findIndex(
      (d) => d.radioKey === item.radioKey
    );
    if (idx < 0) {
      return;
    }

    if (point !== undefined) {
      point.downloadPage[idx] = item;
      setMarkerCollections([...markerCollections]);
    }
  }

  function changeDataset(newDataset: string) {
    const obj = Object.assign(
      {},
      point?.downloadPage.find((item) => item.radioKey === props.category as string),
      { datasetId: newDataset }
    );
    onDatasetValueChange(obj);
    if (point !== undefined) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set(props.category as string, newDataset);
      navigate(`/?${searchParams.toString()}`);
      point.dataset = newDataset;
      setMarkerCollections([...markerCollections]);
      if (showMesh) {
        props.onMeshControl(newDataset);
      }
      dispatch({
        type: 'REPLACE_DATASET_ID', payload: {
          pointId: point?.id!,
          oldId: props.previewDatasetId,
          newId: newDataset
        }
      });
    }
    dispatch({
      type: 'SET_TEMP_DATASET', payload: {
      pointId: point?.id!,
        datasetId: props.previewDatasetId,
        label: point?.label!,
        name: ''
      }
    });
  }

  function onDatasetChange(value: string) {
    const datasetTask = enrichedMetadata.find(
      (task: IEnrichedMetadata) =>
        task.DatasetId === props.previewDatasetId &&
        task.CoordinatesString === props?.coordinates.join()
    );
    if (datasetTask === undefined) {
      withLoading(() => changeDataset(value), loadingContext, {
        source: 'DownloadInfo.tsx - changeDataset',
      });
    } else {
      dispatch({
        type: 'SET_TEMP_DATASET', payload: {
          pointId: point?.id!,
          datasetId: props.previewDatasetId,
          label: point?.label!,
          name: value
        }
      });
      setIsChangeDatasetWarningVisible((prevState: { [key: string]: boolean }) => ({
        ...prevState,
        [props.category as string]: true
      }));
    }
  }

  useEffect(() => {
    if (props.previewDatasetId && props.downloadItems.length > 0) {
      const selectedDatasets = getSelectedDatasets();
      setDatasets(selectedDatasets);
      props.setDatasetHeadline(
        selectedDatasets.find((d) => d.id === props.previewDatasetId)?.label ||
        ''
      );
      resetAdvanceAnalytics()
      getOneYearPreviewSeriesData(props.previewDatasetId);
    }
  }, [props.downloadItems, props.previewDatasetId, props.coordinates, props.pointName, timeStepInterval]);

  function getSelectedDatasets() {
    const briefs = datasetBriefs;
    const selectedDatasets = props.downloadItems.map(
      (item: DownloadInfoItem) => {
        const category =
          item.radioKey === 'SW'
            ? DATASET_CATEGORIES.WAVES
            : item.radioKey === 'HD'
              ? DATASET_CATEGORIES.OCEAN
              : DATASET_CATEGORIES.ATMOSPHERE;
        const brief = briefs.find((b) => b.Id === item.datasetId);
        return {
          label: category,
          id: item.datasetId,
          value: brief?.Acronym || category.toString(),
          key: item.radioKey
        };
      }
    );
    return selectedDatasets.filter((item) => item.id !== '');
  }

  function getKeyByValue(value: string) {
    const index = Object.values(PREPROCESSED_ANALYTICS).indexOf(
      value as unknown as PREPROCESSED_ANALYTICS
    );

    const key = Object.keys(PREPROCESSED_ANALYTICS)[index];

    return key;
  }

  useEffect(() => {
    if (analyticsDefinition) {
      const analytcsDefs = analyticsDefinition.split('|')
      const analytics = analytcsDefs.map((analyticDef: string) => {
        return {
          id: analyticDef,
          name: analyticDef,
          basic: basicAnalytics.includes(analyticDef),
          disabled: basicAndPreprocessedAnalytics.includes(analyticDef) || isTnw
            ? false
            : props.userGroupPermissions?.analyticsList ===
            AnalyticsList.limited,
        } as IAnalytic;
      });
      const findActiveAnalytics = analytics.find((item) => item.id === activePlotAnalytics[0])
      if (!findActiveAnalytics) {
        setActivePlotAnalytics(firstPreviewAnalytics)
      }
      setAvailableAnalytics(
        analytics.filter(
          (analytic: IAnalytic) =>
            analytic.name !== getKeyByValue(PREPROCESSED_ANALYTICS.XLS)
        )
      ); // remove the XLS from Analytics (as it is now replaced by ‘Reports’)
    }
  }, [
    analyticsDefinition,
    user,
    props.userGroupPermissions,
    isTnw
  ]);

  const [updatedTSData, setUpdatedTSData] = useState([]);
  const [loadingUpdateTS, setLoadingUpdateTS] = useState(false)
  const [errorTs, setErrorTs] = useState('')
  const onUpdateTS = async (values: IGetBasisChartV2ApiPayload) => {
    try {
      setLoadingUpdateTS(true)
      setUpdatedTSData([])
      setErrorTs('')
      if (enrichedMetadata) {
        const category = datasets.find(
          (bd: IBestDataset) => bd.id === values.datasetName
        );

        if (category !== undefined) {
          const data = await Api.chart.getBasicChartV2(
            crypto.randomUUID(),
            point?.label ?? '',
            values.datasetName,
            values.startDate,
            values.endDate,
            props.coordinates.join(),
            values.datasetName === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60,
            values.parameters,
          );
          if(data?.Data?.Items){
            setUpdatedTSData(data.Data.Items)
          }else {
            setUpdatedTSData([]);
          }
          trackUpdate(EVENT_ANALYTICS_TYPE_UPDATED, props.coordinates, values.datasetName, BASIC_ANALYTICS.TIME_SERIES.toLowerCase());
        }
      }
    } catch (error: any) {
      setErrorTs(error?.message as string ?? 'Error updating timeseries data')
    } finally {
      setLoadingUpdateTS(false)
    }
  };

  const [updateRosePlotData, setUpdatedRosePlotData] =
    useState<RosePlotChart | null>(null);
  const [errorRosePlot, setErrorRosePlot] = useState('')
  const [loadingRosePlot, setLoadingRosePlot] = useState(false)
  const onUpdateRosePlot = async (values: IGetRosePlotV2ApiPayload) => {
    try {
      setUpdatedRosePlotData(null)
      setErrorRosePlot('')
      setLoadingRosePlot(true)
      if (enrichedMetadata) {
        const category = datasets.find(
          (bd: IBestDataset) => bd.id === values.datasetName
        );
        if (category !== undefined) {
          const data = await Api.chart.getRosePlotV2(
            crypto.randomUUID(),
            point?.label ?? '',
            values.datasetName,
            values.startDate,
            values.endDate,
            props.coordinates.join(),
            values.datasetName === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60,
            values.legendInterval,
            values.valueColumnName,
            values.directionColumnName,
            values.degreeInterval,
          );
          if(data){
            setUpdatedRosePlotData(data);
            trackUpdate(EVENT_ANALYTICS_TYPE_UPDATED, props.coordinates, values.datasetName, BASIC_ANALYTICS.ROSE_PLOT.toLowerCase());
          }
          else {
            setErrorRosePlot('Error updating rose plot')
          }
        }
      }
    } catch (error: any) {
      setErrorRosePlot(error?.response?.data?.Message ?? 'Error updating rose plot')
    } finally {
      setLoadingRosePlot(false)
    }
  };

  const [updatedScatterPlotData, setUpdatedScatterPlotData] = useState([]);
  const [loadingScatterPlot, setLoadingScatterPlot] = useState(false)
  const [errorScatterPlot, setErrorScatterPlot] = useState('')
  const onUpdateScatterPlot = async (values: IGetBasisChartV2ApiPayload) => {
    try {
      setLoadingScatterPlot(true)
      setErrorScatterPlot('')
      const data = await Api.chart.getBasicChartV2(
        crypto.randomUUID(),
        point?.label ?? '',
        values.datasetName,
        values.startDate,
        values.endDate,
        props.coordinates.join(),
        values.datasetName === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60,
        values.parameters,
      );
      if(data?.Data?.Items){
        setUpdatedScatterPlotData(data.Data.Items)
      } else {
        setErrorScatterPlot('Error getting scatter plot data')
        setUpdatedScatterPlotData([]);
      }
      trackUpdate(EVENT_ANALYTICS_TYPE_UPDATED, props.coordinates, values.datasetName, BASIC_ANALYTICS.SCATTER_PLOT.toLowerCase());

    } catch (e: any){
      setErrorScatterPlot(e?.response?.data?.Message ?? 'Error getting scatter plot')
    } finally {
      setLoadingScatterPlot(false)
    }
  };

  const onUpdateJointScatterPlot = async (
    values: IGetJointScatterPlotV2ApiPayload
  ) => {
    try {
      setLoadingScatterPlot(true)
      setErrorScatterPlot('')
      const data = await Api.chart.getJointScatterPlotV2(
        crypto.randomUUID(),
        values.datasetName,
        values.parameters,
        crypto.randomUUID(),
        values.datasetName2,
        values.parameters2,
        values.startDate,
        values.endDate,
        point?.label ?? '',
        props.coordinates.join(),
        values.datasetName === GLOBAL_HD_DTU_ID || values.datasetName2 === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60
      );

      if (data?.Data?.Items) {
        setUpdatedScatterPlotData(data.Data.Items)
      } else {
        setUpdatedScatterPlotData([])
        setErrorScatterPlot('Error getting scatter plot data')
      }
    } catch (e: any){
      setErrorScatterPlot(e?.response?.data?.Message ?? 'Error getting scatter plot')
    } finally {
      setLoadingScatterPlot(false)
    }
  };

  function checkForId(stringId: string, stringIdArr: string[]) {
    if (stringId && stringIdArr && stringIdArr.length > 0) {
      return stringIdArr.includes(stringId);
    }
    return false;
  }

  const onUpdateStatistics = (payload: IAdvancedAnalyticsApiPayload) => {
    setStatiscticsLinks([])
    setStatisticsError('')
    getAdvancedAnalyticData(payload, ADVANCED_ANALYTICS.STATISTICS)
    trackUpdate(EVENT_ANALYTICS_TYPE_UPDATED, props.coordinates, payload.DatasetId, ADVANCED_ANALYTICS.STATISTICS);
  }
  const renderStatistics = () => {
    if (activePlotAnalytics.includes('Statistics') && !datasetIsOffline) {
      return (
        <Statistics
          previewDatasetId={props.previewDatasetId}
          coordinates={props.coordinates}
          datasets={categoryDataset}
          onDatasetChange={onDatasetChange}
          dateRange={
            checkForId(
              props.previewDatasetId,
              props.userGroupPermissions.confidentialDatasetIds
            ) || isDwf
              ? props.userGroupPermissions.dateRange
              : props.userGroupPermissions.analyticsDateRange
          }
          enrichedMetadata={enrichedMetadata}
          onUpdate={onUpdateStatistics}
          statisticsImages={statisticImage}
          statisticsValuesLinks={statisticsLinks}
          error={statisticsError}
          pointName={props.pointName}
          updating={isUpdating}
          userInput={userInput}
          updateUserInput={updateUserInput}
          point={point}
          listNavigation={listNavigation}
          onChangeActiveKey={handleChangePoint}
          pointOption={pointOptions}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
        />
      );
    }
  }

  const renderTSPlotBox = () => {
    if (activePlotAnalytics.includes('Time Series') && yearPreview) {
      return (
        <TSPlot
          listNavigation={listNavigation}
          onDatasetChange={onDatasetChange}
          previewDatasetId={props.previewDatasetId}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
          previewDatasetTimeRange={
            checkForId(
              props.previewDatasetId,
              props.userGroupPermissions.confidentialDatasetIds
            ) || isTnw || isDwf
              ? props.userGroupPermissions.dateRange
              : props.userGroupPermissions.analyticsDateRange
          }
          datasets={categoryDataset}
          defaultValueShortName={yearPreview.DefaultValueShortName}
          variables={
            yearPreview.ItemsInfo
              ? yearPreview.ItemsInfo
              : []
          }
          dateRange={[
            yearPreview.StartDate,
            yearPreview.EndDate,
          ]}
          tsData={
            yearPreview.Data == null
              ? []
              : yearPreview.Data.Items
          }
          onUpdate={onUpdateTS}
          updatedTSData={updatedTSData}
          pointProps={props.pointProps}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          errorUpdate={errorTs}
          loadingButtonUpdate={loadingPreviewTsPlot || loadingUpdateTS}
          onChangeActiveKey={handleChangePoint}
          pointOption={pointOptions}
          point={point}
        />
      );
    }
    return null;
  };

  const renderRosePlotBox = () => {
    if (activePlotAnalytics.includes('Rose Plot')) {

      if (rosePlotPreviewError) {
        return <>{rosePlotPreviewError}</>
      }

      if (!rosePlotPreview) {
        return
      }

      const range = setDateRangeLimit(
        moment(rosePlotPreview.RosePlot.StartDate),
        moment(rosePlotPreview.RosePlot.EndDate),
        true
      );

      return (
        <RosePlot
          previewDatasetId={props.previewDatasetId}
          previewDatasetTimeRange={
            checkForId(
              props.previewDatasetId,
              props.userGroupPermissions.confidentialDatasetIds
            ) || isTnw || isDwf
              ? props.userGroupPermissions.dateRange
              : props.userGroupPermissions.analyticsDateRange
          }
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
          onDatasetChange={onDatasetChange}
          datasets={categoryDataset}
          defaultValueShortName={rosePlotPreview.DefaultValueShortName}
          defaultDirectionShortName={rosePlotPreview.DefaultDirectionShortName}
          variables={rosePlotPreview.RosePlot.ItemsInfo ? rosePlotPreview.RosePlot.ItemsInfo : []}
          dateRange={[range[0].toISOString(), range[1].toISOString()]}
          plotData={rosePlotPreview.RosePlot}
          plotDefaultInterval={defaultRosePlotDirection}
          onUpdateRosePlot={onUpdateRosePlot}
          updatedRosePlotData={updateRosePlotData}
          setUpdatedRosePlotData={setUpdatedRosePlotData}
          pointProps={props.pointProps}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          errorUpdate={errorRosePlot || rosePlotPreviewError}
          listNavigation={listNavigation}
          updating={loadingRosePlot}
          onChangeActiveKey={handleChangePoint}
          pointOption={pointOptions}
          point={point}
        />
      );
    }
    return null;
  };

  const renderScatterPlotBox = () => {
    if (activePlotAnalytics.includes('Scatter Plot') && yearPreview) {
      return (
        <ScatterPlot
          // eslint-disable-next-line react/prop-types
          coordinates={props.coordinates}
          previewDatasetId={props.previewDatasetId}
          previewDatasetTimeRange={
            checkForId(
              props.previewDatasetId,
              props.userGroupPermissions.confidentialDatasetIds
            ) || isTnw || isDwf
              ? props.userGroupPermissions.dateRange
              : props.userGroupPermissions.analyticsDateRange
          }
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
          updating={loadingScatterPlot}
          error={errorScatterPlot}
          categoryDataset={categoryDataset}
          onDatasetChange={onDatasetChange}
          defaultValueShortName={yearPreview.DefaultValueShortName}
          defaultDirectionShortName={yearPreview.DefaultDirectionShortName}
          variables={
            yearPreview.ItemsInfo
              ? yearPreview.ItemsInfo
              : []
          }
          dateRange={[
            yearPreview.StartDate,
            yearPreview.EndDate,
          ]}
          tsData={
            yearPreview.Data == null
              ? []
              : yearPreview.Data.Items
          }
          onUpdateScatterPlot={onUpdateScatterPlot}
          updatedScatterPlotData={updatedScatterPlotData}
          onUpdateJointScatterPlot={onUpdateJointScatterPlot}
          pointProps={props.pointProps}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          gridCellSize={props.gridCellSize}
          waterDepth={props.waterDepth}
          listNavigation={listNavigation}
          onChangeActiveKey={handleChangePoint}
          pointOption={pointOptions}
          point={point}
        />
      );
    }
    return null;
  };

  const [scatterTableError, setScatterTableError] = useState('');
  const [updatingScatterTable , setUpdatingScatterTable ] = useState(false)
  async function getScatterTableData(payLoad: IScatterTableApiPayload) {
    setScatterTableError('')
    setUpdatingScatterTable(true)
    const error = 'Failed to return scatter table data';
    try {
      const result = await Api.chart.getScatterTable({
        ...payLoad
      });
      if (result) {
        if (result.Data) {
          setScatterTableData(result.Data);
          setScatterTableLinks(result.DownloadLinks);
        } else {
          setScatterTableError(
            result.Error && result.Error.ErrorMessage
              ? result.Error.ErrorMessage
              : error
          );
        }
      } else {
        setScatterTableError(error);
      }
    }catch (e) {
      setScatterTableError(error)
    }finally {
      setUpdatingScatterTable(false)
    }
  }

  const onUpdateScatterTable = useCallback(
    (payload: IScatterTableApiPayload) => {
      getScatterTableData(payload);
      trackUpdate(EVENT_ANALYTICS_TYPE_UPDATED, props.coordinates, payload.DatasetId, 'scatter table');
    },
    []
  );
  const renderScatterTable = () => {
    if (activePlotAnalytics.includes('Scatter Table') && enrichedMetadata && !datasetIsOffline) {
      return (
        <ScatterTable
          // eslint-disable-next-line react/prop-types
          previewDatasetId={props.previewDatasetId}
          coordinates={props.coordinates}
          categoryDataset={categoryDataset}
          enrichedMetadata={enrichedMetadata}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
          data={scatterTableData}
          dateRange={
            checkForId(
              props.previewDatasetId,
              props.userGroupPermissions.confidentialDatasetIds
            ) || isTnw || isDwf
              ? props.userGroupPermissions.dateRange
              : props.userGroupPermissions.analyticsDateRange
          }
          updating={updatingScatterTable}
          onUpdate={onUpdateScatterTable}
          onDatasetChange={onDatasetChange}
          error={scatterTableError}
          scatterTableLinks={scatterTableLinks}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          gridCellSize={props.gridCellSize}
          waterDepth={props.waterDepth}
          listNavigation={listNavigation}
          onChangeActiveKey={handleChangePoint}
          pointOption={pointOptions}
          point={point}
        />
      );
    }
    return null;
  };

  const [tidalImage, setTidalImage] = useState('')
  const [statisticImage, setStatisticsImage] = useState('')
  const [exceedanceValuesImage, setExceedanceValuesImage] = useState('')
  const [extremeValuesImage, setExtremeValuesImage] = useState('');
  const [persistenceImage, setPersistenceImage] = useState('');
  const [histogramImage, setHistogramImage] = useState('');
  const [exceedanceValuesLinks, setExceedanceValuesLinks] = useState<string[]>([])
  const [extremeValuesLinks, setExtremeValuesLinks] = useState<string[]>([]);
  const [persistenceLinks, setPersistenceLinks] = useState<string[]>([]);
  const [histogramLinks, setHistogramLinks] = useState<string[]>([]);
  const [statisticsLinks, setStatiscticsLinks] = useState<string[]>([]);
  const [extremeValuesError, setExtremeValuesError] = useState('');
  const [persistenceError, setPersistenceError] = useState('');
  const [histogramError, setHistogramError] = useState('');
  const [exceedanceError, setExceedanceError] = useState('');
  const [statisticsError, setStatisticsError] = useState('');
  const [tidalError, setTidalError] = useState('');
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [advancedAnalyticType, setAdvancedAnalyticType] = useState<ADVANCED_ANALYTICS>(ADVANCED_ANALYTICS.STATISTICS);
  const showErrorFromSignalR = useRef<boolean>(false)

  function resetAdvanceAnalytics(){
    setExtremeValuesImage('')
    setExtremeValuesError('')
    setExtremeValuesLinks([])
    setExceedanceValuesImage('')
    setExceedanceError('')
    setExceedanceValuesLinks([])
    setStatisticsImage('')
    setStatisticsError('')
    setStatiscticsLinks([])
    setHistogramImage('')
    setHistogramError('')
    setHistogramLinks([])
    setTidalImage('')
    setTidalError('')
    setTidalLevelLinks([])
    setPersistenceImage('')
    setPersistenceError('')
    setPersistenceLinks([])
  }

  useEffect(() => {
    if (messageReceived) {
      const splitMessage = receivedMessageText.split(':');
      let hubMessage: string;

      if (splitMessage.length > 1) {
        hubMessage = splitMessage[splitMessage.length - 1];
      } else {
        hubMessage = receivedMessageText;
      }

      if (hubMessage && hubMessage.toLowerCase() !== 'success') {
        setErrorMessage(advancedAnalyticType, hubMessage);
      }
      setIsUpdating(false);
      showErrorFromSignalR.current = true
      resetMessageState();
    } else {
      showErrorFromSignalR.current = false
    }
  }, [messageReceived]);

  async function getAdvancedAnalyticData(
    payload: IAdvancedAnalyticsApiPayload,
    type: ADVANCED_ANALYTICS,
  ) {
    try {
      setIsUpdating(true)
      setAdvancedAnalyticType(type);
      // We add PlogFlag to return download links for analytics in jpg and txt format
      const result = await Api.chart.getAdvancedAnalytics({
        ...payload,
        PlotFlag: type === ADVANCED_ANALYTICS.HISTOGRAM ? '2' : '[1 3]',
      });
      if (result) {
        if (result.Result && result.Result.img) {
          switch (type) {
            case ADVANCED_ANALYTICS.PERSISTENCE_WEATHERWINDOW:
              setPersistenceImage('data:image/png;base64,' + result.Result.img);
              setPersistenceLinks(result.Result.links);
              break;
            case ADVANCED_ANALYTICS.EXTREME_VALUES:
              setExtremeValuesImage('data:image/png;base64,' + result.Result.img);
              setExtremeValuesLinks(result.Result.links);
              break;
            case ADVANCED_ANALYTICS.HISTOGRAM:
              setHistogramImage('data:image/png;base64,' + result.Result.img);
              setHistogramLinks(result.Result.links);
              break;
            case ADVANCED_ANALYTICS.EXCEEDANCE:
              setExceedanceValuesImage('data:image/png;base64,' + result.Result.img)
              setExceedanceValuesLinks(result.Result.links)
              break
            case ADVANCED_ANALYTICS.STATISTICS:
              setStatisticsImage('data:image/png;base64,' + result.Result.img)
              setStatiscticsLinks(result.Result.links)
              break
            case ADVANCED_ANALYTICS.TIDAL_LEVEL:
              setTidalImage('data:image/png;base64,' + result.Result.img)
              setTidalLevelLinks(result.Result.links)
              break
            default:
          }
        } else {
          if (!showErrorFromSignalR.current) {
            const errorMessage =
              result.Error && result.Error.ErrorMessage
                ? result.Error.ErrorMessage
                : 'Failed to return data';
            setErrorMessage(type, errorMessage);
          }
        }
      }
    } finally {
      setIsUpdating(false);
    }
  }

  const setErrorMessage = (type: ADVANCED_ANALYTICS, errorMessage: string) => {
    switch (type) {
      case ADVANCED_ANALYTICS.PERSISTENCE_WEATHERWINDOW:
        setPersistenceImage('')
        setPersistenceError(errorMessage);
        break;
      case ADVANCED_ANALYTICS.EXTREME_VALUES:
        setExtremeValuesImage('')
        setExtremeValuesError(errorMessage);
        break;
      case ADVANCED_ANALYTICS.HISTOGRAM:
        setHistogramImage('')
        setHistogramError(errorMessage);
        break;
      case ADVANCED_ANALYTICS.EXCEEDANCE:
        setExceedanceValuesImage('')
        setExceedanceError(errorMessage)
        break
      case ADVANCED_ANALYTICS.STATISTICS:
        setStatisticsImage('')
        setStatisticsError(errorMessage)
        break
      case ADVANCED_ANALYTICS.TIDAL_LEVEL:
        setTidalImage('')
        setTidalError(errorMessage)
        break
      default:
    }
  }

  const onUpdateTidalLevel = (payload: IAdvancedAnalyticsApiPayload) => {
    setTidalLevelLinks([])
    setTidalError('')
    getAdvancedAnalyticData(payload, ADVANCED_ANALYTICS.TIDAL_LEVEL)
    trackUpdate(EVENT_ANALYTICS_TYPE_UPDATED, props.coordinates, payload.DatasetId, ADVANCED_ANALYTICS.TIDAL_LEVEL);
  }
  const renderTidalLevel = () => {
    if (activePlotAnalytics.includes('Tidal Levels') && enrichedMetadata && !datasetIsOffline) {
      return (
        <TidalLevel
          previewDatasetId={props.previewDatasetId}
          coordinates={props.coordinates}
          datasets={categoryDataset}
          dateRange={
            checkForId(
              props.previewDatasetId,
              props.userGroupPermissions.confidentialDatasetIds
            ) || isDwf
              ? props.userGroupPermissions.dateRange
              : props.userGroupPermissions.analyticsDateRange
          }
          enrichedMetadata={enrichedMetadata}
          updating={isUpdating}
          onUpdate={onUpdateTidalLevel}
          tidalLevelValueImage={tidalImage}
          tidalValueLinks={tidalLevelLinks}
          error={tidalError}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          onDatasetChange={onDatasetChange}
          point={point}
          listNavigation={listNavigation}
          onChangeActiveKey={handleChangePoint}
          pointOption={pointOptions}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
        />
      )
    }
  }


  const onUpdatePersistence = (payload: IAdvancedAnalyticsApiPayload) => {
    setPersistenceError('');
    setPersistenceLinks([]);
    getAdvancedAnalyticData(
      payload,
      ADVANCED_ANALYTICS.PERSISTENCE_WEATHERWINDOW
    );
    trackUpdate(EVENT_ANALYTICS_TYPE_UPDATED, props.coordinates, payload.DatasetId, ADVANCED_ANALYTICS.PERSISTENCE_WEATHERWINDOW);
  };

  const renderPersistence = () => {
    if (activePlotAnalytics.includes('Weather windows') &&!datasetIsOffline) {
      return (
        <Persistence
          // eslint-disable-next-line react/prop-types
          updating={isUpdating}
          previewDatasetId={props.previewDatasetId}
          coordinates={props.coordinates}
          categoryDataset={categoryDataset}
          dateRange={
            checkForId(
              props.previewDatasetId,
              props.userGroupPermissions.confidentialDatasetIds
            ) || isTnw || isDwf
              ? props.userGroupPermissions.dateRange
              : props.userGroupPermissions.analyticsDateRange
          }
          enrichedMetadata={enrichedMetadata}
          onUpdate={onUpdatePersistence}
          persistenceImage={persistenceImage}
          error={persistenceError}
          persistenceLinks={persistenceLinks}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          onDatasetChange={onDatasetChange}
          gridCellSize={props.gridCellSize}
          waterDepth={props.waterDepth}
          point={point}
          listNavigation={listNavigation}
          onChangeActiveKey={handleChangePoint}
          pointOption={pointOptions}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
        />
      );
    }
    return null;
  };

  const onUpdateExtremevalues = (payload: IAdvancedAnalyticsApiPayload) => {
    setExtremeValuesError('');
    setExtremeValuesLinks([]);
    getAdvancedAnalyticData(payload, ADVANCED_ANALYTICS.EXTREME_VALUES);
    trackUpdate(EVENT_ANALYTICS_TYPE_UPDATED, props.coordinates, payload.DatasetId, ADVANCED_ANALYTICS.EXTREME_VALUES);
  };

  const renderExtremeValues = () => {
    if (activePlotAnalytics.includes('Extreme Values') && !datasetIsOffline) {
      return (
        <ExtremeValues
          // eslint-disable-next-line react/prop-types
          previewDatasetId={props.previewDatasetId}
          coordinates={props.coordinates}
          datasets={categoryDataset}
          dateRange={
            checkForId(
              props.previewDatasetId,
              props.userGroupPermissions.confidentialDatasetIds
            ) || isTnw || isDwf
              ? props.userGroupPermissions.dateRange
              : props.userGroupPermissions.analyticsDateRange
          }
          enrichedMetadata={enrichedMetadata}
          onUpdate={onUpdateExtremevalues}
          extremeValuesImage={extremeValuesImage}
          error={extremeValuesError}
          extremeValuesLinks={extremeValuesLinks}
          pointName={props.pointName}
          updating={isUpdating}
          userInput={userInput}
          updateUserInput={updateUserInput}
          onDatasetChange={onDatasetChange}
          point={point}
          listNavigation={listNavigation}
          onChangeActiveKey={handleChangePoint}
          pointOption={pointOptions}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
        />
      );
    }
    return null;
  };

  const onUpdateHistogram = (payload: IAdvancedAnalyticsApiPayload) => {
    setHistogramError('');
    setHistogramLinks([]);
    getAdvancedAnalyticData(payload, ADVANCED_ANALYTICS.HISTOGRAM);
    trackUpdate(EVENT_ANALYTICS_TYPE_UPDATED, props.coordinates, payload.DatasetId, ADVANCED_ANALYTICS.HISTOGRAM);
  };

  const renderHistogram = () => {
    if ((activePlotAnalytics.includes('Histogram') ||
      activePlotAnalytics.includes('Histogram (w. Weibull Parameters)')) && !datasetIsOffline
    ) {
      return (
        <Histogram
          // eslint-disable-next-line react/prop-types
          previewDatasetId={props.previewDatasetId}
          coordinates={props.coordinates}
          datasets={datasets}
          dateRange={
            checkForId(
              props.previewDatasetId,
              props.userGroupPermissions.confidentialDatasetIds
            ) || isTnw || isDwf
              ? props.userGroupPermissions.dateRange
              : props.userGroupPermissions.analyticsDateRange
          }
          enrichedMetadata={enrichedMetadata}
          onUpdate={onUpdateHistogram}
          updating={isUpdating}
          histogramImage={histogramImage}
          histogramLinks={histogramLinks}
          error={histogramError}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          point={point}
          listNavigation={listNavigation}
          onDatasetChange={onDatasetChange}
          onChangeActiveKey={handleChangePoint}
          pointOption={pointOptions}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
        />
      );
    }
    return null;
  };

  const onUpdateExceedance = (payload: IAdvancedAnalyticsApiPayload) => {
    setExceedanceValuesLinks([])
    setExceedanceError('')
    getAdvancedAnalyticData(payload, ADVANCED_ANALYTICS.EXCEEDANCE)
    trackUpdate(EVENT_ANALYTICS_TYPE_UPDATED, props.coordinates, payload.DatasetId, ADVANCED_ANALYTICS.EXCEEDANCE);
  }

  const renderExceedance = () => {
    if (activePlotAnalytics.includes('Exceedance') && !datasetIsOffline) {
      return (
        <Exceedance
          updating={isUpdating}
          previewDatasetId={props.previewDatasetId}
          coordinates={props.coordinates}
          datasets={categoryDataset}
          dateRange={
            checkForId(
              props.previewDatasetId,
              props.userGroupPermissions.confidentialDatasetIds
            ) || isDwf
              ? props.userGroupPermissions.dateRange
              : props.userGroupPermissions.analyticsDateRange
          }
          enrichedMetadata={enrichedMetadata}
          onUpdate={onUpdateExceedance}
          exceedanceValuesImage={exceedanceValuesImage}
          exceedanceValuesLinks={exceedanceValuesLinks}
          error={exceedanceError}
          pointName={props.pointName}
          userInput={userInput}
          updateUserInput={updateUserInput}
          onDatasetChange={onDatasetChange}
          point={point}
          listNavigation={listNavigation}
          onChangeActiveKey={handleChangePoint}
          pointOption={pointOptions}
          isForAllAnalytics={isForAllAnalytics}
          setIsForAllAnalytics={setIsForAllAnalytics}
          globalDateRange={analyticsDateTime}
          setGlobalDateRange={setAnalyticsDateTime}
        />
      )
    }
  }

  const listNavigation = (
    <div className={styles.listNavigation}>
      {availableAnalytics.map((analytic, index) => {
        return (
          <div
          className={`${analytic.disabled || updatingScatterTable || isUpdating ? styles.disabled : ''} ${activePlotAnalytics.includes(analytic.id) ? styles.active : ''
          }`}
          key={`element-${index}-${point?.id}`}
          onClick={() => analytic.disabled || updatingScatterTable || isUpdating ? null : handleAnalyticsTypeChanged(analytic)}
          >
            {analytic.name}
            <span></span>
          </div>
        );
      })}
    </div>
  )
  return (
    <>
      {loadingPreviewTsPlot ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%'
        }}>
          <Spin size='large' />
        </div>
      ) : (
        <>
          {renderTSPlotBox()}
          {renderRosePlotBox()}
          {renderScatterPlotBox()}
          {renderScatterTable()}
          {renderPersistence()}
          {renderExtremeValues()}
          {renderHistogram()}
          {renderTidalLevel()}
          {renderStatistics()}
          {renderExceedance()}
        </>
      )}
    </>
);
}
